import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateSubBranchRequestSchema,
  type CreateSubBranchRequest,
  type SubBranchDetail,
  type SubBranchListItem,
  UpdateSubBranchRequestSchema,
  type UpdateSubBranchRequest,
} from '@utazzo/shared';
import {
  UzButton,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSelect,
  UzSwitch,
  UzTextarea,
} from '@utazzo/ui';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { useCreateSubBranch, useUpdateSubBranch } from '../use-sub-branches';

import { deriveCode, useAutoDeriveCode } from '@/components/master-code-field';
import { useBranchesList } from '@/features/branches/use-branches';

interface SubBranchFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  subBranch?: SubBranchDetail | SubBranchListItem | null;
}

const CREATE_DEFAULTS: CreateSubBranchRequest = {
  code: '',
  name: '',
  branchId: '',
  address: '',
  sortOrder: 0,
  isActive: true,
};

interface BranchOption {
  value: string;
  label: string;
}

/**
 * Field set shared by create + edit. Reads the form via context
 * (UzFormField → useFormContext), so it renders inside either UzForm.
 */
const SubBranchFields = ({
  pending,
  branchOptions,
  branchesLoading,
}: {
  pending: boolean;
  branchOptions: ReadonlyArray<BranchOption>;
  branchesLoading: boolean;
}): React.JSX.Element => (
  <>
    <UzFormField name="name">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Name"
          placeholder="e.g. Andheri Desk"
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="branchId">
      {({ field, error }) => (
        <UzSelect<string>
          name={field.name}
          label="Parent branch"
          required
          placeholder={
            branchesLoading
              ? 'Loading…'
              : branchOptions.length === 0
                ? 'No active branches'
                : 'Select branch'
          }
          options={branchOptions}
          value={(field.value as string | null | undefined) || undefined}
          onValueChange={field.onChange}
          error={error}
          hint="The Branch this sub-branch belongs to (Masters → Branches)."
          disabled={pending || branchesLoading}
        />
      )}
    </UzFormField>
    <UzFormField name="address">
      {({ field, error }) => (
        <UzTextarea
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Address"
          placeholder="Optional. Street / building, area, city."
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="sortOrder">
      {({ field, error }) => (
        <UzNumberInput
          name={field.name}
          label="Sort order"
          precision={0}
          value={field.value === null || field.value === undefined ? '' : (field.value as number)}
          onValueChange={(v) => field.onChange(v ?? 0)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="isActive">
      {({ field }) => (
        <UzSwitch
          label="Active"
          description="Inactive sub-branches are hidden from user assignment forms."
          checked={Boolean(field.value)}
          onCheckedChange={field.onChange}
          disabled={pending}
        />
      )}
    </UzFormField>
  </>
);

export const SubBranchFormDrawer = ({
  open,
  onOpenChange,
  mode,
  subBranch,
}: SubBranchFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createSubBranch = useCreateSubBranch();
  const updateSubBranch = useUpdateSubBranch();
  const pending = createSubBranch.isPending || updateSubBranch.isPending;

  // Parent-branch picker. Active rows only, large page size so the
  // dropdown shows the whole Branch master in one fetch.
  const branchesQuery = useBranchesList({
    page: 1,
    pageSize: 200,
    sortBy: 'sortOrder',
    sortDir: 'asc',
    isActive: true,
  });
  const branchOptions = useMemo<BranchOption[]>(
    () => (branchesQuery.data?.data ?? []).map((b) => ({ value: b.id, label: b.name })),
    [branchesQuery.data],
  );

  const createForm = useForm<CreateSubBranchRequest>({
    resolver: zodResolver(CreateSubBranchRequestSchema),
    defaultValues: CREATE_DEFAULTS,
  });
  const updateForm = useForm<UpdateSubBranchRequest>({
    resolver: zodResolver(UpdateSubBranchRequestSchema),
    defaultValues: {},
  });

  useAutoDeriveCode(createForm, isCreate);

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset(CREATE_DEFAULTS);
    } else if (subBranch) {
      // `code` is intentionally omitted - it's the stable machine
      // identifier and is read-only post-create.
      updateForm.reset({
        name: subBranch.name,
        branchId: subBranch.branchId ?? '',
        address: subBranch.address ?? '',
        sortOrder: subBranch.sortOrder,
        isActive: subBranch.isActive,
      });
    }
  }, [open, isCreate, subBranch, createForm, updateForm]);

  const onCreate = createForm.handleSubmit(async (values) => {
    // `code` is derived from the name (hidden field).
    await createSubBranch.mutateAsync({
      ...values,
      code: deriveCode(values.name),
      address: values.address?.trim() ? values.address : null,
    });
    onOpenChange(false);
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!subBranch) return;
    await updateSubBranch.mutateAsync({
      id: subBranch.id,
      payload: {
        ...values,
        address: values.address?.trim() ? values.address : null,
      },
    });
    onOpenChange(false);
  });

  const fields = (
    <SubBranchFields
      pending={pending}
      branchOptions={branchOptions}
      branchesLoading={branchesQuery.isLoading}
    />
  );

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'Add sub-branch' : `Edit ${subBranch?.name ?? 'sub-branch'}`}
      description="The name is what agents see."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Submit' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      {isCreate ? (
        <UzForm form={createForm} onSubmit={onCreate}>
          {fields}
        </UzForm>
      ) : (
        <UzForm form={updateForm} onSubmit={onUpdate}>
          {fields}
        </UzForm>
      )}
    </UzDrawer>
  );
};
